import { Button } from '@tunasong/ui-lib'
import { RecordFabButton } from '@tunasong/audio-ui'
import { graphHooks } from '@tunasong/graph-lib/react'
import { Add } from '@tunasong/icons'
import { EntityFilters, shortUuid } from '@tunasong/models'
import { getAcceptedMimeTypes, useGlobalAppContext, usePlugins } from '@tunasong/plugin-lib'
import { isFolderContainer } from '@tunasong/schemas'
import { useSearch } from '@tunasong/search'
import {
  ContentContainer,
  DndList,
  DropZone,
  EntityGrid,
  Typography,
  useDimensions,
  useNavigateToEntity,
} from '@tunasong/ui-lib'
import { useCurrentUser } from '@tunasong/redux'
import cn from 'classnames'
import type { FC, MouseEvent } from 'react'
import { ContinueWorkingSection } from './continue-working-section.js'
import { MergedContinueSection } from './merged-continue-section.js'
import { PinnedSection } from './pinned-section.js'
import { RecentlyUpdatedSection } from './recently-updated-section.js'
import styles from './root-main.module.css'

const SPACES_ANCHOR_ID = 'dashboard-spaces'

const scrollToSpaces = () => {
  const el = document.getElementById(SPACES_ANCHOR_ID)
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}

export interface RootMainProps {}

export const RootMain: FC<RootMainProps> = props => {
  const {} = props
  const { userId } = useCurrentUser()
  const { isTiny } = useDimensions()

  const navigateToEntity = useNavigateToEntity()

  const { results: sharedSearchResults, searching } = useSearch({ queryType: 'shared', query: userId, size: 500 })
  /** Filter out results that has a parentId as well */
  const sharedResultIds = new Set([...sharedSearchResults.map(r => r.id)])

  const { entities: ownedEntities, isLoading: isLoadingEntities } = graphHooks.useEntities({
    root: true,
    filter: EntityFilters.folder,
  })
  const ownedResultIds = new Set([...ownedEntities.map(r => r.id)])

  const sharedResults = sharedSearchResults
    .filter(r => (r.parentId ? !sharedResultIds.has(r.parentId) : true))
    .filter(r => !ownedResultIds.has(r.id))

  const allEntities = [...ownedEntities, ...sharedResults]

  const spaces = allEntities
    // Only show top-level entities
    .filter(e => !Boolean(e.parentId) || e.parentId === 'ROOT')
    .filter(isFolderContainer)
    // Filter out spaces that are public. We may want to surface those in a different way
    // .filter(e => !hasPublicAccess(e.acls))
    .sort((a, b) => (a.name ?? '').localeCompare(b?.name ?? ''))
  const spacesIds = new Set([...spaces.map(r => r.id)])

  const graph = graphHooks.useEntityGraph()
  // Cached-graph create used by the empty-state "New Song" CTA (also populates the graph cache)
  const { createEntity: createSong } = graphHooks.useEntityCreate()

  const addSpace = async (ev: MouseEvent) => {
    ev.preventDefault()

    const space = await graph
      .createEntity({ parent: null, entity: { type: 'folder', name: 'New Space', tags: ['space'] } })
      .unwrap()
    if (space) {
      navigateToEntity(space)
    }
  }
  const busyCreating = graph.createEntityResult.isLoading

  const inbox = ownedEntities.filter(r => !spacesIds.has(r.id))

  const plugins = usePlugins('all')
  const mimeTypes = getAcceptedMimeTypes(plugins)
  const globalContext = useGlobalAppContext()

  // Empty-state CTA for the Continue-working section. Creates a song at root and
  // navigates the user straight into it. Lands in the dashboard's `Inbox` row
  // (top-level entities that aren't Spaces). User can move it into a Space later.
  //
  // Mirrors the CreateEntityDialog flow (apps/tunasong/tunasong-ui/src/entity/
  // create-entity-dialog.tsx:49) so the new song is fully scaffolded: plugin
  // template provides the seed yDoc + sectionType; we set a `shortUuid()` id up
  // front; template.onCreate is invoked if the song plugin ever adds one;
  // navigation prefers the plugin's `Edit` layout so the user lands in the editor.
  const addSongFromEmptyState = async () => {
    const songPlugin = plugins?.find(p => p.node?.type === 'song')
    const template = songPlugin?.templates?.[0]
    if (!songPlugin || !template) {
      return
    }
    const newEntity = { ...template.template, id: shortUuid(), name: 'New Song' }
    // Resolves once the server has stored the song, so the navigation below finds its collab room (#1895)
    const song = await createSong({ parent: null, entity: newEntity })
    if (!song) {
      return
    }
    const layouts = Object.keys(songPlugin.layouts ?? {})
    const layout = layouts.includes('Edit') ? 'Edit' : undefined
    navigateToEntity(song, { layout })
    if (template.onCreate) {
      // Fire-and-forget: the editor is already mounting at the new entity's path;
      // onCreate side effects (e.g., seed children) can land while the user reads.
      void template.onCreate({ entity: song, parent: undefined, globalContext })
    }
  }

  return (
    <ContentContainer>
      {/* Phase 2: three priority-ordered sections replace the single
       *  "Recently updated" feed. On `tiny`, Pinned + Continue working
       *  share a heading so the small screen doesn't fragment further. */}
      {isTiny ? (
        <MergedContinueSection onOpenSpaces={scrollToSpaces} onNewSong={addSongFromEmptyState} />
      ) : (
        <>
          <PinnedSection />
          <ContinueWorkingSection onOpenSpaces={scrollToSpaces} onNewSong={addSongFromEmptyState} />
        </>
      )}
      <div id={SPACES_ANCHOR_ID} className={styles.spacesWrapper}>
        <Typography className={cn(styles.header, styles.headerSpaces)} variant="h2">
          Spaces
        </Typography>
        <Button
          disabled={busyCreating}
          onClick={addSpace}
          startIcon={<Add />}
          className={styles.addButton}
          color="primary"
          data-testid="create-new-space"
        >
          New Space
        </Button>
      </div>
      <EntityGrid
        size={'large'}
        skeletonItems={9}
        entities={spaces}
        loading={isLoadingEntities || searching}
        onOpen={navigateToEntity}
        placeholder="No spaces found"
      />
      <RecentlyUpdatedSection />
      <Typography className={styles.header} variant="h2">
        Inbox
      </Typography>
      <DndList
        size="large"
        isLoading={isLoadingEntities || searching}
        entities={inbox}
        optionsKey={'root'}
        placeholder="No items in Inbox"
      />
      <div className={styles.spacer} />
      <div className={styles.fabContainer}>
        <RecordFabButton position="relative" color="primary" />
      </div>
      <DropZone parentId={null} accept={mimeTypes} uploadOnClick showClickZone showZone />
    </ContentContainer>
  )
}

export default RootMain
